import type { AuditDependency, Ecosystem } from '../schemas/index';

/**
 * Manifest parsers — pure functions that extract dependencies from pasted or
 * uploaded manifest files. They run in the browser (before `POST /api/v1/audit`)
 * and are heavily unit-tested; no I/O, no DOM.
 *
 * Two kinds of entry come out of a parser:
 *
 *   - exact pins (`4.17.21`), emitted with their `version` filled in;
 *   - version ranges (`^4.17.0`, `[1.0,2.0)`), emitted with `version: null` and
 *     the constraint in `requirement`. The Worker simplifies each range to the
 *     highest published version under its upper bound before auditing (see
 *     `../versions/range.ts`) — parsers stay pure and never resolve anything.
 *
 * Anything that cannot become either (a git URL, a `workspace:` protocol, an
 * unresolved Maven property) goes to `skipped` with a human-readable reason.
 */

/** A dependency the parser could not pin to an exact version. */
export interface SkippedDependency {
  name: string;
  /** The raw requirement string, when one was present. */
  requirement: string | null;
  /** Human-readable reason ("version range", "no version specified"...). */
  reason: string;
}

/** The result of parsing one manifest. */
export interface ParsedManifest {
  system: Ecosystem;
  /**
   * Auditable dependencies in manifest order — exact pins (`version` set) and
   * ranges (`version: null`, `requirement` set) interleaved as written.
   */
  dependencies: AuditDependency[];
  /** Entries that are neither a pin nor a resolvable range. */
  skipped: SkippedDependency[];
}

/** True for a parsed dependency whose version still has to be resolved. */
export function isRangedDependency(d: AuditDependency): boolean {
  return d.version === null;
}

/** A parser for one manifest format. Throws `ManifestParseError` on garbage input. */
export type ManifestParser = (content: string) => ParsedManifest;

/** Thrown when the input is not recognizable as the expected format at all. */
export class ManifestParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ManifestParseError';
  }
}

/** Helper for parsers: builds an exact-version dependency entry. */
export function dep(
  system: Ecosystem,
  name: string,
  version: string,
  requirement: string | null = null,
): AuditDependency {
  return { system, name, version, requirement };
}

/**
 * Helper for parsers: builds a range entry, to be resolved to its upper bound
 * by the Worker. `requirement` must be the constraint alone (`>=1.0,<2.0`),
 * not the surrounding manifest line.
 */
export function rangeDep(system: Ecosystem, name: string, requirement: string): AuditDependency {
  return { system, name, version: null, requirement: requirement.trim() };
}
